import { Checkbox, Select, Skeleton } from 'antd';
import { useTranslation } from 'react-i18next';
import type { HealthMetricType } from '../../fetchHooks/useHealthmetrictypesWithMeasurements';
import { useHealthmetrictypesWithMeasurements } from '../../fetchHooks/useHealthmetrictypesWithMeasurements.ts';
import { getHealthMetricDescription } from '../../utils/utilFunctions';
const { Option } = Select;

interface SelectHealthMetricsDropdownProps {
  selectedGraphs: HealthMetricType[];
  setSelectedGraphs: React.Dispatch<React.SetStateAction<HealthMetricType[]>>;
  showBloodworkDescription: boolean;
  setShowBloodworkDescription: React.Dispatch<React.SetStateAction<boolean>>;
}

export const SelectHealthMetricsDropdown = ({
  selectedGraphs,
  setSelectedGraphs,
  showBloodworkDescription,
  setShowBloodworkDescription,
}: SelectHealthMetricsDropdownProps) => {
  const { t, i18n } = useTranslation();
  const { data: healthmetrictypes, isLoading: isLoadingHealthmetrictypes } = useHealthmetrictypesWithMeasurements();
  if (isLoadingHealthmetrictypes) {
    return <Skeleton.Input active size="default" style={{ width: 300 }} />;
  }

  return (
    <>
      <Select
        defaultValue={selectedGraphs.length === healthmetrictypes?.length ? 'all' : 'none'}
        style={{
          width: 300,
        }}
        showSearch
        allowClear
        onChange={(value: string | number | string[] | number[] | undefined) => {
          if (value === 'all') {
            setSelectedGraphs(healthmetrictypes || []);
            return;
          }
          if (value === 'none' || value === undefined) {
            setSelectedGraphs([]);
            return;
          }
          if (Array.isArray(value)) {
            const selectedMetrics = value
              .map((id) => {
                const idNum = typeof id === 'number' ? id : Number(id);
                return healthmetrictypes?.find((metric) => metric.id === idNum);
              })
              .filter(Boolean) as HealthMetricType[];
            setSelectedGraphs(selectedMetrics);
          } else if (value && value !== 'none' && value !== 'all') {
            const idNum = typeof value === 'number' ? value : Number(value);
            const selectedMetric = healthmetrictypes?.find((metric) => metric.id === idNum);
            setSelectedGraphs(selectedMetric ? [selectedMetric] : []);
          }
        }}
        filterOption={(input, option) => {
          const searchText = input.toLowerCase();
          const keyMatch = option?.key?.toString().toLowerCase().indexOf(searchText) ?? -1;
          const titleMatch = option?.title?.toLowerCase().indexOf(searchText) ?? -1;
          const childrenMatch = (option?.children as any)?.props?.children?.toLowerCase().indexOf(searchText) ?? -1;
          return keyMatch >= 0 || titleMatch >= 0 || childrenMatch >= 0;
        }}
      >
        <Option value={'none'} title="none">
          {t('healthMetric.selectHealthMetricToShow')}
        </Option>
        <Option value="all" title="all">
          {t('healthMetric.showAll')}
        </Option>
        {healthmetrictypes?.map((item) => {
          const description = getHealthMetricDescription(item, i18n.language);
          return (
            <Option key={item.id} value={item.id.toString()} title={description}>
              <span>{description}</span>
            </Option>
          );
        })}
      </Select>
      <Checkbox
        checked={showBloodworkDescription}
        onChange={() => setShowBloodworkDescription((prev) => !prev)}
        style={{ marginLeft: '2rem' }}
      >
        {t('healthMetric.showBloodworkDescription')}
      </Checkbox>
    </>
  );
};
